import type { ContentfulContentEntry } from "#/services/contentful/contentful-content-entry"
import type { Condition } from "#/services/contentful/conditional-gate"
import { toCondition } from "#/lib/contentful/utils/conditionUtils"
import ContentTypes from "#/utils/ContentTypes"
import type {
  ContentNode,
  AccordionNode,
  BlockQuoteNode,
  ContainerNode,
  ContentBlockNode,
  ContentBlockWithIconNode,
  TwoColumnBulletListNode,
  TwoColumnLayoutNode,
  UnorderedListNode,
  ModalNode,
  BannerImageNode
} from "./renderer/types"
import type { Block, Document, Inline } from "@contentful/rich-text-types"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import type { Entry } from "contentful"
import type {
  TypeAccordionSkeleton,
  TypeBlockQuoteSkeleton,
  TypeContainerSkeleton,
  TypeContentBlockSkeleton,
  TypeContentBlockWithIconSkeleton,
  TypeTwoColumnBulletListSkeleton,
  TypeTwoColumnLayoutSkeleton,
  TypeUnorderedListSkeleton,
  TypeModalSkeleton,
  TypeBannerImageSkeleton
} from "#/types/contentful"

export type ConditionEvaluator = (conditions: Condition[]) => boolean

type ResolvedEntry<T extends import("contentful").EntrySkeletonType> = Entry<T, "WITHOUT_UNRESOLVABLE_LINKS", string>

export function filterContent(
  entries: ContentfulContentEntry[],
  doesUserSatisfyConditions: ConditionEvaluator
): ContentNode[] {
  if (!entries) return []

  const result: ContentNode[] = []

  for (const entry of entries) {
    if (!entry.fields) continue

    const conditions = (entry.fields.conditions ?? []).map(toCondition).filter(Boolean)
    if (!doesUserSatisfyConditions(conditions)) continue

    const node = mapEntryToContentNode(entry, doesUserSatisfyConditions)
    if (node) result.push(node)
  }

  return result
}

function mapEntryToContentNode(
  entry: ContentfulContentEntry,
  doesUserSatisfyConditions: ConditionEvaluator
): ContentNode | null {
  const typeId = entry.sys.contentType.sys.id

  switch (typeId) {
    case ContentTypes.ACCORDION:
      return mapAccordion(entry as ResolvedEntry<TypeAccordionSkeleton>, doesUserSatisfyConditions)
    case ContentTypes.BLOCK_QUOTE:
      return mapBlockQuote(entry as ResolvedEntry<TypeBlockQuoteSkeleton>, doesUserSatisfyConditions)
    case ContentTypes.CONTAINER:
      return mapContainer(entry as ResolvedEntry<TypeContainerSkeleton>, doesUserSatisfyConditions)
    case ContentTypes.CONTENT_BLOCK:
      return mapContentBlock(entry as ResolvedEntry<TypeContentBlockSkeleton>, doesUserSatisfyConditions)
    case ContentTypes.CONTENT_BLOCK_WITH_ICON:
      return mapContentBlockWithIcon(entry as ResolvedEntry<TypeContentBlockWithIconSkeleton>)
    case ContentTypes.TWO_COLUMN_BULLET_LIST:
      return mapTwoColumnBulletList(entry as ResolvedEntry<TypeTwoColumnBulletListSkeleton>, doesUserSatisfyConditions)
    case ContentTypes.TWO_COLUMN_LAYOUT:
      return mapTwoColumnLayout(entry as ResolvedEntry<TypeTwoColumnLayoutSkeleton>, doesUserSatisfyConditions)
    case ContentTypes.UNORDERED_LIST:
      return mapUnorderedList(entry as ResolvedEntry<TypeUnorderedListSkeleton>, doesUserSatisfyConditions)
    case ContentTypes.MODAL:
      return mapModal(entry as ResolvedEntry<TypeModalSkeleton>, doesUserSatisfyConditions)
    case ContentTypes.BANNER_IMAGE:
      return mapBannerImage(entry as ResolvedEntry<TypeBannerImageSkeleton>)
    default:
      return null
  }
}

function mapAccordion(entry: ResolvedEntry<TypeAccordionSkeleton>, evaluate: ConditionEvaluator): AccordionNode {
  const { fields } = entry
  return {
    type: "accordion",
    id: entry.sys.id,
    summaryClickableElement: fields.summaryClickableElement,
    content: filterContent(fields.content ?? [], evaluate)
  }
}

function mapBlockQuote(entry: ResolvedEntry<TypeBlockQuoteSkeleton>, evaluate: ConditionEvaluator): BlockQuoteNode {
  const { fields } = entry
  return {
    type: "blockQuote",
    id: entry.sys.id,
    backgroundColor: fields.backgroundColor,
    content: filterContent(fields.content ?? [], evaluate)
  }
}

function mapContainer(entry: ResolvedEntry<TypeContainerSkeleton>, evaluate: ConditionEvaluator): ContainerNode {
  const { fields } = entry
  return {
    type: "container",
    id: entry.sys.id,
    content: filterContent(fields.content ?? [], evaluate)
  }
}

function mapContentBlock(
  entry: ResolvedEntry<TypeContentBlockSkeleton>,
  evaluate: ConditionEvaluator
): ContentBlockNode {
  const { fields } = entry
  return {
    type: "contentBlock",
    id: entry.sys.id,
    body: fields.body,
    richTextBody: fields.richTextBody ? filterRichTextEmbeddedEntries(fields.richTextBody, evaluate) : undefined
  }
}

function mapContentBlockWithIcon(entry: ResolvedEntry<TypeContentBlockWithIconSkeleton>): ContentBlockWithIconNode {
  const { fields } = entry
  return {
    type: "contentBlockWithIcon",
    id: entry.sys.id,
    body: fields.body,
    iconUrl: fields.icon?.fields?.file?.url,
    iconAlignment: fields.iconAlignment ?? "start",
    iconDisplaySize: fields.iconDisplaySize ?? "medium",
    customSize: fields.customSize
  }
}

function mapTwoColumnBulletList(
  entry: ResolvedEntry<TypeTwoColumnBulletListSkeleton>,
  evaluate: ConditionEvaluator
): TwoColumnBulletListNode {
  const { fields } = entry
  return {
    type: "twoColumnBulletList",
    id: entry.sys.id,
    content: filterContent(fields.content ?? [], evaluate)
  }
}

function mapTwoColumnLayout(
  entry: ResolvedEntry<TypeTwoColumnLayoutSkeleton>,
  evaluate: ConditionEvaluator
): TwoColumnLayoutNode {
  const { fields } = entry
  return {
    type: "twoColumnLayout",
    id: entry.sys.id,
    content: filterContent(fields.content ?? [], evaluate)
  }
}

function mapUnorderedList(
  entry: ResolvedEntry<TypeUnorderedListSkeleton>,
  evaluate: ConditionEvaluator
): UnorderedListNode {
  const { fields } = entry
  return {
    type: "unorderedList",
    id: entry.sys.id,
    content: filterContent(fields.content ?? [], evaluate)
  }
}

function mapModal(entry: ResolvedEntry<TypeModalSkeleton>, evaluate: ConditionEvaluator): ModalNode {
  const { fields } = entry
  return {
    type: "modal",
    id: entry.sys.id,
    title: fields.title,
    linkText: fields.linkText,
    content: filterContent(fields.content ?? [], evaluate)
  }
}

function mapBannerImage(entry: ResolvedEntry<TypeBannerImageSkeleton>): BannerImageNode {
  const { fields } = entry
  return {
    type: "bannerImage",
    id: entry.sys.id,
    altText: fields.altText,
    imageUrl: fields.image?.fields?.file?.url
  }
}

function filterRichTextEmbeddedEntries(document: Document, evaluate: ConditionEvaluator): Document {
  const cloned = structuredClone(document)
  filterRichTextNodeContent(cloned, evaluate)
  return cloned
}

function filterRichTextNodeContent(node: Document | Block | Inline, evaluate: ConditionEvaluator): void {
  node.content = node.content.filter((child) => {
    if (child.nodeType === INLINES.EMBEDDED_ENTRY || child.nodeType === BLOCKS.EMBEDDED_ENTRY) {
      const embeddedChild = child
      const target = embeddedChild.data?.target
      if (!target?.sys || !target?.fields) return false

      const conditions = (target.fields.conditions ?? []).map(toCondition).filter(Boolean)
      if (!evaluate(conditions)) return false

      const simplified = mapEntryToContentNode(target, evaluate)
      if (simplified) {
        embeddedChild.data.target = simplified
      }
    }

    if ("content" in child && Array.isArray(child.content)) {
      filterRichTextNodeContent(child, evaluate)
    }
    return true
  })
}
